import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { toast } from "sonner";
import { fmtAmount, equivalentFor } from "@/lib/currency";
import { EngagementBreakdown } from "@/components/EngagementBreakdown";
import { FxDisclaimer } from "@/components/shared/DualPrice";

export default function PublicEngagement() {
  const { token } = useParams();
  const [eng, setEng] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agree, setAgree] = useState(false);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/engagements/public/${token}`);
      setEng(data); setSignature(data.client?.full_name || "");
      if (data.signed) setSigned(true);
    } catch (e) { setError("This engagement link is invalid or has expired."); } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const sign = async () => {
    if (!agree) return toast.error("Please confirm you agree to the terms.");
    if (!signature.trim()) return toast.error("Please type your signature.");
    setBusy(true);
    try {
      await api.post(`/engagements/public/${token}/sign`, { agree: true, typed_signature: signature, accepted_by: signature });
      setSigned(true); toast.success("Engagement letter signed. Thank you!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not sign"); } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 text-center"><div><Icons.AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" /><p className="text-slate-600">{error}</p></div></div>;

  const cc = eng.currency_context || { usd_thb_rate: 33, show_thb: false };
  const pricing = eng.pricing || {};
  const pcur = pricing.currency || "USD";
  const eq = (v) => equivalentFor(v, pcur, { rate: cc.usd_thb_rate, showThb: cc.show_thb });
  const hasFees = pricing.total != null || (eng.engagement_breakdown && Object.keys(eng.engagement_breakdown).length > 0);

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4" data-testid="public-engagement-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center"><Icons.FileCheck className="h-5 w-5 text-white" /></div>
          <span className="font-heading text-xl font-semibold text-slate-800">Expat Tax Firm</span>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-slate-900 text-white px-6 py-5">
            <div className="text-sm text-slate-300">Engagement {eng.engagement_number}</div>
            <h1 className="font-heading text-2xl font-semibold">{eng.title}</h1>
          </div>
          <div className="p-6">
            <div className="border border-slate-100 rounded-lg p-5 bg-white text-sm text-slate-700 leading-relaxed max-h-[45vh] overflow-y-auto tf-scroll"
              data-testid="engagement-letter-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(eng.letter_body) }} />

            {hasFees && (
              <div className="mt-5 border border-slate-200 rounded-xl p-4" data-testid="engagement-fee-summary">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 mb-2">Engagement Fees</h3>
                {pricing.total != null && (
                  <div className="flex flex-col items-end gap-1 text-sm">
                    {pricing.subtotal != null && <div className="text-slate-500">Subtotal <span className="font-medium text-slate-800 ml-3">{fmtAmount(pricing.subtotal, pcur)}</span>{eq(pricing.subtotal) && <span className="text-[11px] text-slate-400 ml-1">≈ {eq(pricing.subtotal)}</span>}</div>}
                    {pricing.discount > 0 && <div className="text-slate-500">Discount <span className="font-medium text-rose-600 ml-3">-{fmtAmount(pricing.discount, pcur)}</span></div>}
                    <div className="text-lg font-semibold text-slate-900">Total <span className="ml-3">{fmtAmount(pricing.total, pcur)}</span>{eq(pricing.total) && <span className="text-[11px] text-slate-400 ml-2 font-normal">≈ {eq(pricing.total)}</span>}</div>
                  </div>
                )}
                <EngagementBreakdown breakdown={eng.engagement_breakdown} rate={cc.usd_thb_rate} showThb={cc.show_thb} />
                {(eq(pricing.total) || cc.show_thb) && !(eng.engagement_breakdown && Object.keys(eng.engagement_breakdown).length > 0) && <FxDisclaimer className="mt-2 text-right" />}
              </div>
            )}

            {signed ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center mt-5" data-testid="public-engagement-signed">
                <Icons.CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
                <div className="font-medium text-emerald-800">Engagement Letter Signed</div>
                <p className="text-sm text-emerald-700">Thank you! Our team will begin your onboarding shortly.</p>
              </div>
            ) : (
              <div className="border-t border-slate-100 pt-5 mt-5">
                <label className="text-xs text-slate-500">Type your full name to sign</label>
                <input value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="sign-input"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm mt-1 font-serif italic text-lg" />
                <label className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg p-3 my-3">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" data-testid="sign-agree" />
                  <span className="font-medium">I have reviewed and agree to the terms of this Engagement Letter.</span>
                </label>
                <button onClick={sign} disabled={busy} data-testid="sign-btn"
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg py-3 font-medium flex items-center justify-center gap-2">
                  {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.PenTool className="h-4 w-4" />}Sign Engagement Letter
                </button>
              </div>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-4">Secured by Tax Flow · Expat Tax Firm</p>
      </div>
    </div>
  );
}
